import { PlusIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import type { BracketConfigEntryDTO, CarrierWeightBracketDTO } from '@invenco/shipping-interface';

import { Button } from '@/components/core/button';
import { CardFooter } from '@/components/core/card';
import { EditableDataTable, useEditableRowDataTable } from '@/components/data-table';
import { DataTableCardBody } from '@/components/data-table/data-table-card';
import { Result } from '@/shared/helpers/Result';
import { useMessages } from '@/shared/providers/MessagesProvider';

import { type BracketConfigDraft, createBracketDraft } from './bracket-draft';
import { bracketColumns, bracketRowSchema } from './carrier-weight-bracket-columns';

// Stable, deterministic ids for persisted rows so a background refetch (e.g. react-query's
// refetch-on-window-focus) while a row is being edited doesn't remint ids and drop the in-flight
// edit. Weights are unique within a config, so the weight is a safe key; new unsaved rows get their
// own ids from createBracketDraft (different prefix, so no collision).
function toDrafts(config: BracketConfigEntryDTO[] | undefined): BracketConfigDraft[] {
  return (config ?? []).map((entry) => ({
    id: `config-${entry.bracket}`,
    bracket: entry.bracket,
    cubicAllowance: entry.cubicAllowance,
  }));
}

const toEntry = (row: BracketConfigDraft): BracketConfigEntryDTO => {
  if (row.bracket === null) {
    // Unreachable in practice (persisted rows always have a weight, and the edited row is validated
    // before it reaches here) — but fail loud rather than silently POST a null weight if that ever
    // changes.
    throw new Error('Cannot persist a weight bracket without a weight');
  }
  return { bracket: row.bracket, cubicAllowance: row.cubicAllowance };
};

type Props = {
  config?: BracketConfigEntryDTO[];
  /**
   * Persists the full bracket config. The table is one editable *view* of a single config — it owns
   * row editing and the client-side validation, but not the gateway call, so the owner decides how
   * the config is saved (see `useCarrierAccount`).
   */
  onSave: (bracketsConfig: BracketConfigEntryDTO[]) => Promise<Result<CarrierWeightBracketDTO>>;
};

/**
 * One weight-bracket config (the account-level default or a single service) as an inline editable
 * table — the body and an "add bracket" footer only, with no card chrome of its own: the owning
 * `WeightBracketsCard` provides the surrounding card and tabs. A row is not a backend record (the
 * whole config is one `CarrierWeightBracket`), so every per-row save/delete re-persists the full
 * `bracketsConfig` via `onSave`. Editing is serialised to one row at a time, so these full-config
 * writes never race.
 */
export function CarrierWeightBracketTable({ config, onSave }: Props) {
  const messages = useMessages();

  const rows = useMemo(() => toDrafts(config), [config]);
  const [draftRow, setDraftRow] = useState<BracketConfigDraft | null>(null);
  const renderedRows = useMemo(() => (draftRow ? [...rows, draftRow] : rows), [draftRow, rows]);

  const { table, editingRowId, startEditingRow, onClickRow, onCancelEdit, ...editableProps } =
    useEditableRowDataTable({
      data: renderedRows,
      columns: bracketColumns,
      schema: bracketRowSchema,
      // This table has no pagination controls, so opt out of client pagination — otherwise
      // TanStack's default 10-row page size silently hides rows (including the draft row).
      manualPagination: true,
      manualSorting: false,
      onSaveRow: async (row, values) => {
        const nextEntries = renderedRows.map((r) =>
          r.id === row.id
            ? { bracket: values.bracket, cubicAllowance: values.cubicAllowance }
            : toEntry(r),
        );

        const weights = nextEntries.map((entry) => entry.bracket);
        if (new Set(weights).size !== weights.length) {
          // Load-bearing: useEditableRowDataTable keeps the row open on Result.fail but surfaces no
          // message itself, so this toast is the user's only feedback for a duplicate weight.
          messages.error('Each weight must be unique');
          return Result.fail('Duplicate bracket weight');
        }

        nextEntries.sort((a, b) => a.bracket - b.bracket);

        const result = await onSave(nextEntries);
        if (result.isFailure) return result;
        if (row.id === draftRow?.id) setDraftRow(null);
        return result;
      },
      onDeleteRow: async (row) => {
        if (row.id === draftRow?.id) {
          setDraftRow(null);
          return undefined;
        }
        const remaining = rows.filter((r) => r.id !== row.id);
        if (remaining.length === 0) {
          // A config must keep at least one bracket: an empty bracketsConfig means "no brackets
          // configured" at quote time (and the update schema requires minItems: 1). Block the
          // delete rather than corrupt the config; removing a whole service config is a separate flow.
          messages.error('A weight bracket config must keep at least one bracket');
          return Result.fail('Cannot delete the last bracket');
        }
        return onSave(remaining.map(toEntry));
      },
    });

  const handleClickAdd = () => {
    const draft = createBracketDraft();
    setDraftRow(draft);
    if (draft.id) startEditingRow(draft.id, draft, { columnId: 'bracket' });
  };

  // Clicking away from the in-progress new row abandons it (it was never persisted).
  const handleClickRow: typeof onClickRow = (row, event) => {
    if (draftRow && row.id !== draftRow.id) setDraftRow(null);
    onClickRow(row, event);
  };

  const handleCancelEdit = () => {
    if (draftRow && editingRowId === draftRow.id) setDraftRow(null);
    onCancelEdit();
  };

  return (
    <>
      <DataTableCardBody table={table} emptyMessage="No weight brackets yet">
        <EditableDataTable
          table={table}
          editingRowId={editingRowId}
          {...editableProps}
          onClickRow={handleClickRow}
          onCancelEdit={handleCancelEdit}
        />
      </DataTableCardBody>
      <CardFooter className="flex h-10 items-stretch bg-card p-0">
        <Button
          type="button"
          variant="outline"
          className="h-full w-full justify-start rounded-none border-0 px-4 shadow-none"
          onClick={handleClickAdd}
          disabled={!!draftRow}
        >
          <PlusIcon />
          Add bracket
        </Button>
      </CardFooter>
    </>
  );
}
